import React, { useState, useContext } from "react";
import styled, { keyframes } from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { useInitPayment } from "../../services/api/subscription-api";
import { ToastContext } from "../../services/contexts/toast-context";

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--bg-main);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem 1rem;
  font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
  position: relative;
`;

const BackButton = styled.button`
  position: absolute;
  top: 2rem;
  left: 2rem;
  background: var(--btn-primary);
  border: 2px solid var(--btn-primary);
  border-radius: 12px;
  padding: 1rem 1.5rem;
  color: white;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  z-index: 10;

  &:hover {
    background: var(--btn-primary-hover);
    border-color: var(--btn-primary-hover);
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 768px) {
    top: 1rem;
    left: 1rem;
    padding: 0.75rem 1rem;
    font-size: 0.9rem;
  }
`;

const PurchaseCard = styled.section`
  background: var(--bg-card);
  border-radius: 18px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  max-width: 420px;
  width: 100%;
  padding: 2.5rem 2rem 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${fadeInUp} 0.6s ease forwards;
`;

const ProductIcon = styled.div`
  background: var(--btn-primary);
  width: 96px;
  height: 96px;
  border-radius: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 800;
  font-size: 3.8rem;
  color: white;
  user-select: none;
  margin-bottom: 1.5rem;
  box-shadow: 0 4px 12px var(--btn-primary);
`;

const Title = styled.h1`
  color: var(--text-main);
  font-weight: 700;
  font-size: 1.9rem;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  text-align: center;
  line-height: 1.4;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border-radius: 12px;
  border: 2px solid var(--border-color);
  background: var(--bg-main);
  color: var(--text-main);
  margin-bottom: 2rem;
  outline-offset: 2px;
  cursor: pointer;
  transition: border-color 0.3s ease;

  &:focus {
    border-color: var(--btn-primary);
    box-shadow: 0 0 12px var(--btn-primary);
  }
`;

const PromoCodeInput = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border-radius: 12px;
  border: 2px solid var(--border-color);
  background: var(--bg-main);
  color: var(--text-main);
  margin-bottom: 2rem;
  outline-offset: 2px;
  transition: border-color 0.3s ease;

  &::placeholder {
    color: var(--text-secondary);
  }

  &:focus {
    border-color: var(--btn-primary);
    box-shadow: 0 0 12px var(--btn-primary);
  }
`;

const ProceedButton = styled.button`
  background: var(--btn-primary);
  color: white;
  font-weight: 700;
  font-size: 1.3rem;
  padding: 1.1rem 0;
  width: 100%;
  border: none;
  border-radius: 14px;
  cursor: pointer;
  transition: background-color 0.3s ease, transform 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--btn-primary-hover);
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TermsText = styled.p`
  margin-top: 1.8rem;
  font-size: 0.85rem;
  color: var(--text-secondary);
  text-align: center;

  a {
    color: var(--btn-primary);
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
`;

// Helper to parse query string
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function PurchasePage() {
  const query = useQuery();
  const toastRef = useContext(ToastContext);
  const navigate = useNavigate();

  const type = query.get("type") || "credit"; // default to credit purchase
  const bundleQuery = query.get("bundle"); // small, medium, large
  const templateId = query.get("id");
  const name = query.get('name')
  const templatePrice = Number(query.get('price') || 0);


  // Credit bundles you offer
  const creditBundles = [
    { id: "1", label: "10 Credits - $10", price: 10, credits: 10 },
    { id: "2", label: "30 Credits - $25", price: 25, credits: 30 },
    { id: "3", label: "100 Credits - $70", price: 100, credits: 100 },
  ];

  // Template info (would normally fetch by id)
  const templateProduct = {
    title: `${name}`,
    subtitle:
      "A premium, fully customizable template for your project needs.",
    price: templatePrice || 0,
    icon: "T",
  };

  // State for selected credit bundle (default to medium or bundle from query)
  const defaultBundle =
    creditBundles.find((b) => b.id === bundleQuery) || creditBundles[1];
  const [selectedBundle, setSelectedBundle] = useState(defaultBundle);
  const [promoCode, setPromoCode] = useState(null);
  const [loading, setLoading] = useState(false);
  const { mutate: initPayment } = useInitPayment()

  // The product to display & buy depends on purchase type
  const product = type === "template" ? templateProduct : selectedBundle;

  const handleBundleChange = (e) => {
    const bundle = creditBundles.find((b) => b.id === e.target.value);
    if (bundle) setSelectedBundle(bundle);
  };

  const handleBack = () => {
    // Use browser history to go back to the actual previous page
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback if no history (direct link access)
      if (type === "credit") {
        navigate('/recruiter/home');
      } else {
        navigate('/default/templates');
      }
    }
  };

  const handlePurchase = () => {
    setLoading(true);
    const paymentData = {
      item_type: type === "template" ? "template" : "creditbundle",
      item_id: type === "template" ? Number(templateId || 1) : selectedBundle.id,
      phone: "+21600000000"
    };
    
    // Only include promo_code if it has a value
    if (promoCode && promoCode.trim()) {
      paymentData.promo_code = promoCode.trim();
    }
    
    initPayment(paymentData, {
      onSuccess: (data) => {
        setLoading(false);
        if (data.data.type === "success") {
          toastRef.current.show({ severity: 'success', summary: 'Success', detail: "Purchase done successfully (Promo code applied)", life: 5000 });
          handleBack()
        }
        if (data.data.payUrl) {
          // Redirect user to payment gateway after a short delay (or immediately)
          window.location.href = data.data.payUrl;
        } else {
          toastRef.current.show({ severity: 'error', summary: 'Error', detail: "Payment initialization failed: No payment URL returned.", life: 3000 });
        }
      },
      onError: (error) => {
        setLoading(false);
        toastRef.current.show({ severity: 'error', summary: 'Error', detail: JSON.stringify(error.response.data.error), life: 3000 });
      }
    });
  };
  return (
    <PageWrapper>
      <BackButton onClick={handleBack}>
        <span>←</span>
        Back
      </BackButton>
      <PurchaseCard>
        <ProductIcon>{product.icon || "C"}</ProductIcon>
        <Title>{product.title || `${product.credits} Credits`}</Title>
        <Subtitle>{product.subtitle || "Add credits to unlock more features."}</Subtitle>

        {/* Show dropdown only for credit purchase */}
        {type === "credit" && (
          <Select
            value={selectedBundle.id}
            onChange={handleBundleChange}
            disabled={loading}
            aria-label="Select credit bundle"
          >
            {creditBundles.map((bundle) => (
              <option key={bundle.id} value={bundle.id}>
                {bundle.label}
              </option>
            ))}
          </Select>
        )}

        <PromoCodeInput
          placeholder="Have a promo code? (optional)"
          value={promoCode}
          onChange={(e) => setPromoCode(e.target.value)}
          disabled={loading}
          aria-label="Promo code input"
        />

        <ProceedButton onClick={handlePurchase} disabled={loading}>
          {loading
            ? "Redirecting..."
            : `Proceed to Payment - $${product.price.toFixed(2)}`}
        </ProceedButton>

        <TermsText>
          By continuing, you agree to our{" "}
          <a href="/terms" target="_blank" rel="noopener noreferrer">
            Terms & Conditions
          </a>
          .
        </TermsText>
      </PurchaseCard>
    </PageWrapper>
  );
}
